import React from "react";
import { Link } from "react-router-dom";
import PolicyLayout, { EmailLink } from "./PolicyLayout";

function Terms() {
  return (
    <PolicyLayout
      title="terms of service"
      intro="These terms govern use of the tunrisefield website and purchases made through the store."
    >
      <div className="policy-callout">
        <p>
          Nothing in these terms limits a right or remedy that cannot lawfully be limited under the
          consumer laws that apply to you.
        </p>
      </div>

      <h2>1. About these terms</h2>
      <p>
        By using the website or submitting an order, you agree to these terms and the policies linked
        from our <Link to="/policies">Policy Center</Link>. If you make a purchase for an organization,
        you confirm that you are authorized to bind it. If you do not agree, do not use the store.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must have the legal capacity to enter a purchase contract in your location. If local law
        permits a minor to use the website only with permission, a parent or guardian must supervise
        the use and is responsible for the transaction.
      </p>

      <h2>3. Product information</h2>
      <p>
        We aim to describe products, materials, dimensions, colors, and compatibility accurately.
        Screens and lighting can affect how colors appear, and small manufacturing variations may
        occur. Images are illustrative and the written description for the selected variant forms part
        of the order. Please contact us before ordering if a feature is essential to your decision.
      </p>

      <h2>4. Prices, taxes, and availability</h2>
      <p>
        Store prices are shown in USD unless stated otherwise. Delivery charges and any tax collected
        by us are displayed before payment. Your bank or payment provider may apply conversion or
        international transaction fees. Products are subject to availability. For more information,
        read <Link to="/pricing">Pricing, Taxes &amp; Duties</Link>.
      </p>

      <h2>5. Orders and contract formation</h2>
      <p>
        Adding an item to the cart does not reserve it. Submitting payment is an offer to buy. An
        automated payment or order message confirms receipt, not necessarily acceptance. We accept
        an order when we send a dispatch confirmation or otherwise expressly confirm acceptance.
        We may reject or cancel an order for stock, payment, pricing, security, sanctions, delivery,
        or legal reasons. If we cancel after payment, we will refund the affected amount.
      </p>

      <h2>6. Payment</h2>
      <p>
        Payment is completed through a hosted third-party checkout. You authorize the amount shown
        at checkout and confirm that the payment information you use is valid and authorized. We may
        perform proportionate verification or fraud checks. See our <Link to="/payments">Payment Policy</Link>.
      </p>

      <h2>7. Delivery, risk, and title</h2>
      <p>
        Delivery estimates are not guarantees unless we expressly say otherwise. Risk transfers as
        required by applicable law, generally when you or a person you designate receives the goods.
        Ownership transfers after full payment and delivery, unless local law provides otherwise. Read
        our <Link to="/shipping">Shipping &amp; Delivery Policy</Link> for tracking, customs, and delay procedures.
      </p>

      <h2>8. Cancellations, returns, and defects</h2>
      <p>
        Order changes and cancellations are described in our <Link to="/orders">Order &amp; Cancellation Policy</Link>.
        Returns and refunds are governed by our <Link to="/returns">Returns &amp; Refund Policy</Link>.
        Faulty products may also be covered by statutory rights and our <Link to="/warranty">Product Warranty Policy</Link>.
      </p>

      <h2>9. Acceptable use</h2>
      <p>
        You may use the website only for lawful personal or legitimate business shopping. You must not
        disrupt the site, misuse checkout, introduce malicious code, scrape protected content in a way
        that harms the service, impersonate another person, or infringe anyone’s rights. Our
        <Link to="/acceptable-use"> Acceptable Use Policy</Link> forms part of these terms.
      </p>

      <h2>10. Intellectual property and non-affiliation</h2>
      <p>
        Original tunrisefield names, text, graphics, and site materials are protected to the extent
        provided by law. Rights in third-party names, products, and materials remain with their
        respective owners. Appearance on this site does not imply sponsorship or endorsement unless
        expressly stated. See our <Link to="/intellectual-property">Intellectual Property Policy</Link>.
      </p>

      <h2>11. User messages and submissions</h2>
      <p>
        You retain ownership of material you send us. You give us a limited right to use it only as
        reasonably necessary to answer your request, process an order or claim, protect the store, or
        comply with law. Do not submit content you do not have the right to share. We do not claim an
        unrestricted license to your ideas, photographs, or other creative work.
      </p>

      <h2>12. Third-party services and links</h2>
      <p>
        Payment, delivery, and other features may be provided by independent services. Their terms may
        apply when you use them. We are not responsible for unrelated content on an external site, but
        this does not remove any responsibility we have to you for our own sale of goods.
      </p>

      <h2>13. Service availability</h2>
      <p>
        We may maintain, modify, or discontinue website features. We do not promise uninterrupted or
        error-free access. We will not make changes that retroactively remove rights from an accepted
        order unless the law permits and the change is reasonably necessary.
      </p>

      <h2>14. Our responsibility</h2>
      <p>
        We do not exclude or limit liability where doing so would be unlawful, including liability for
        fraud, willful misconduct, or injury caused by negligence where it cannot be excluded. Subject
        to those rules, we are not responsible for losses that were not reasonably foreseeable when the
        contract was formed, or for business losses arising from a consumer purchase. Any limitation
        applies only to the maximum extent permitted by law.
      </p>

      <h2>15. Your responsibility</h2>
      <p>
        You are responsible for the accuracy of order details you provide, protecting your device and
        payment credentials, and following product instructions. You are responsible for loss caused
        by your unlawful misuse of the website, but not for matters outside your reasonable control.
      </p>

      <h2>16. Events outside reasonable control</h2>
      <p>
        Neither party is responsible for delay caused by events it could not reasonably prevent, such
        as natural disasters, widespread carrier disruption, government action, conflict, or major
        infrastructure failure. We will take reasonable steps to reduce the impact and will provide the
        cancellation or refund options required by law.
      </p>

      <h2>17. Governing rules and disputes</h2>
      <p>
        These terms are governed by the law that applies to the store operator, without depriving a
        consumer of mandatory protections in the country or region where they live. Courts or dispute
        bodies with mandatory authority retain that authority. We do not require individual arbitration
        or waive class or collective rights through these terms. See our <Link to="/complaints">Complaints &amp; Dispute Policy</Link>.
      </p>

      <h2>18. General terms</h2>
      <p>
        If part of these terms is unenforceable, the remaining parts continue to apply. A delay in
        enforcing a right is not a waiver. You may not transfer an order without our consent where the
        transfer would materially affect fulfillment; we may transfer our rights as part of a genuine
        business reorganization only where your protections are not reduced.
      </p>

      <h2>19. Changes and contact</h2>
      <p>
        Updated terms apply from the revision date and do not retroactively change accepted orders
        unless required by law. Contact <EmailLink subject="Terms of service question" /> with questions.
      </p>
    </PolicyLayout>
  );
}

export default Terms;
